import React from 'react'
import { toast } from 'react-toastify'

import { Badge, IconButton, Tooltip, useTheme } from '@material-ui/core'
import { Flag } from '@material-ui/icons'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import MessageIcon from '@material-ui/icons/Message'
import AddIcon from '@material-ui/icons/Add'
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn'

import { Container, Attachment } from '../../../components'
import { FlagInventoryInspectionData } from '../../../hooks/useFlagInventoryInspections'
import { patchInventoryInspection } from '../../../store'
import { useAppDispatch } from '../../../hooks'
import { ReactComponent as ActionItemIcon } from '../../../assets/icons/action-item.svg'
import { typographyStyle } from '../../../theme/typographyStyle'

// Constants
const LEFT_COL_W = 400

// Helper to get area display name
export const getAreaDisplayName = (
    item: FlagInventoryInspectionData,
): string => {
    const areaConfig = item.area_inspection?.area?.area_config?.name ?? ''
    const areaLabel = item.area_inspection?.area?.area_label ?? ''

    if (areaConfig && areaLabel) {
        return `${areaConfig} (${areaLabel})`
    }
    return areaConfig || areaLabel || 'Unknown Area'
}

interface FlaggedInspectionRowProps {
    item: FlagInventoryInspectionData
    messageDrawerOpen?: boolean
    onOpenMessages: (channelId: number) => void
    updateInvInsState: (invIns: FlagInventoryInspectionData) => void
    onCreateWorkorder: (item: FlagInventoryInspectionData) => void
}

export const FlaggedInspectionRow: React.FC<FlaggedInspectionRowProps> = ({
    item,
    messageDrawerOpen,
    onOpenMessages,
    updateInvInsState,
    onCreateWorkorder,
}) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const folderPath = item.area_inspection?.area?.unit?.folder_path ?? ''
    const folderName = item.area_inspection?.area?.unit?.folder_name ?? ''
    const unitName = item.area_inspection?.area?.unit?.name ?? 'Unknown Unit'
    const areaName = getAreaDisplayName(item)
    const inventoryName = item.inventory_config?.name ?? 'Unknown Item'
    const statusName = item.status?.name ?? 'Unknown'
    const statusColor = item.status?.color ?? theme.palette.grey[500]
    const isHandled = item.handled
    const inspectionName = item.area_inspection?.inspection?.name ?? ''

    let statusIcon: JSX.Element | null
    if (item.handled) {
        statusIcon = (
            <IconButton
                onClick={() => {
                    dispatch(
                        patchInventoryInspection({
                            inventoryInspectionId: item.id,
                            body: {
                                handled: false,
                            },
                        }),
                    ).then(() => {
                        toast.success(
                            'Inventory inspection marked as not handled',
                        )
                        const updatedItem: FlagInventoryInspectionData = {
                            ...item,
                            handled: false,
                        }
                        updateInvInsState(updatedItem)
                    })
                }}
                color="primary"
            >
                <Tooltip title="Mark Not Handled">
                    <AssignmentTurnedInIcon />
                </Tooltip>
            </IconButton>
        )
    } else {
        statusIcon = (
            <IconButton
                onClick={() => {
                    dispatch(
                        patchInventoryInspection({
                            inventoryInspectionId: item.id,
                            body: {
                                handled: true,
                            },
                        }),
                    ).then(() => {
                        toast.success('Inventory inspection marked as handled')
                        const updatedItem: FlagInventoryInspectionData = {
                            ...item,
                            handled: true,
                        }
                        updateInvInsState(updatedItem)
                    })
                }}
                color="secondary"
            >
                <Tooltip title="Mark Handled">
                    <Flag />
                </Tooltip>
            </IconButton>
        )
    }

    return (
        <div
            style={{
                display: 'flex',
                backgroundColor: messageDrawerOpen
                    ? theme.palette.primary.light
                    : isHandled
                    ? theme.palette.grey[100]
                    : 'white',
                borderRadius: theme.shape.borderRadius,
                border: `1px solid ${theme.palette.grey[300]}`,
                minHeight: 180,
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
            }}
        >
            {/* Handled indicator */}
            {isHandled && (
                <div
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 10,
                    }}
                >
                    <Tooltip title="Handled">
                        <CheckCircleIcon
                            style={{ color: theme.palette.success.main }}
                            fontSize="small"
                        />
                    </Tooltip>
                </div>
            )}

            {/* Left Column: Location & Item Details */}
            <Container
                style={{
                    padding: theme.spacing(2),
                    minWidth: LEFT_COL_W,
                    maxWidth: LEFT_COL_W,
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRight: `1px solid ${theme.palette.grey[200]}`,
                }}
            >
                {/* Top content */}
                <div>
                    {/* Top row: Location and Inspection Name */}
                    <Container
                        style={{
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        {/* Location info */}
                        <div style={{ flex: 1 }}>
                            {/* Folder Path */}
                            {folderPath && folderName && (
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.85rem',
                                        display: 'block',
                                        marginBottom: 2,
                                    }}
                                >
                                    {folderPath}
                                    {folderName}
                                </span>
                            )}
                            {/* Unit and Area */}
                            <Container
                                style={{
                                    alignItems: 'center',
                                    gap: theme.spacing(1),
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span
                                    style={{
                                        ...typographyStyle(theme.typography.h6),
                                        fontWeight: 700,
                                        fontSize: '1.15rem',
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {unitName}
                                </span>
                                <span
                                    style={{
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    •
                                </span>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body1,
                                        ),
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.95rem',
                                    }}
                                >
                                    {areaName}
                                </span>
                            </Container>
                        </div>

                        {/* Inspection Name - top right */}
                        {inspectionName && (
                            <Tooltip title={inspectionName}>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.caption,
                                        ),
                                        color: theme.palette.text.secondary,
                                        backgroundColor:
                                            theme.palette.grey[200],
                                        padding: '2px 8px',
                                        borderRadius: 4,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 150,
                                        flexShrink: 0,
                                        marginLeft: theme.spacing(1),
                                    }}
                                >
                                    {inspectionName}
                                </span>
                            </Tooltip>
                        )}
                    </Container>

                    {/* Inventory Item - larger, no background */}
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 500,
                            fontSize: '1.1rem',
                            color: theme.palette.text.primary,
                            display: 'block',
                            marginTop: theme.spacing(1),
                        }}
                    >
                        {inventoryName}
                    </span>
                </div>

                {/* Footer: Status & Actions */}
                <Container
                    style={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Status Badge */}
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: theme.spacing(0.5, 1.5),
                            borderRadius: 16,
                            backgroundColor: statusColor,
                            color: 'white',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                        }}
                    >
                        {statusName}
                    </span>

                    {/* Actions */}
                    <Container style={{ alignItems: 'center', gap: 4 }}>
                        {item.workorder_ids && item.workorder_ids.length > 0 ? (
                            <Tooltip title={'Has Workorder'}>
                                <ActionItemIcon height={20} width={20} />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Create Workorder">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onCreateWorkorder(item)
                                    }}
                                >
                                    <AddIcon
                                        style={{
                                            color: theme.palette.primary.main,
                                            fontSize: 20,
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                        {statusIcon}
                        {item.channel ? (
                            <Tooltip title="Messages">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onOpenMessages(item.channel ?? 0)
                                    }}
                                >
                                    <Badge
                                        badgeContent={
                                            item.area_inspection
                                                ?.message_count ?? 0
                                        }
                                        color="secondary"
                                    >
                                        <MessageIcon
                                            style={{
                                                color:
                                                    theme.palette.primary.main,
                                                fontSize: 22,
                                            }}
                                        />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <div
                                style={{ width: 26, backgroundColor: 'red' }}
                            />
                        )}
                    </Container>
                </Container>
            </Container>

            {/* Right Column: Attachments */}
            <Container
                style={{
                    padding: theme.spacing(1.5),
                    flex: 1,
                    alignItems: 'center',
                    overflowX: 'auto',
                    gap: theme.spacing(1.5),
                    overflowY: 'hidden',
                }}
            >
                {item.attachments && item.attachments.length > 0 ? (
                    item.attachments.map((attachment) => (
                        <div
                            key={attachment.id}
                            style={{
                                flexShrink: 0,
                                borderRadius: theme.shape.borderRadius,
                                overflow: 'hidden',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Attachment
                                attachment={attachment.file}
                                uploaded={attachment.uploaded}
                                theme={theme}
                                size={150}
                            />
                        </div>
                    ))
                ) : (
                    <Container
                        style={{
                            flex: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.text.disabled,
                            fontStyle: 'italic',
                            fontSize: '0.875rem',
                        }}
                    >
                        No attachments
                    </Container>
                )}
            </Container>
        </div>
    )
}
